import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TrackByFunction,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { SafeHtml } from '@angular/platform-browser';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  ActionListData,
  ChatMsgVM,
  MessagePart,
} from '../../../models/message.types';
import {
  AiChatActionHandlerService,
  MarkdownRendererService,
} from '../../../services';
import { AlloTableDirective } from '../../../directives/allo-table.directive';
import { StreamRevealDirective } from '../../../directives/stream-reveal.directive';
import { AiChatActionCardComponent } from '../ai-chat-action-card/ai-chat-action-card.component';
import { AiChatActionListComponent } from '../ai-chat-action-list/ai-chat-action-list.component';
import { AiChatChartCardComponent } from '../ai-chat-chart-card/ai-chat-chart-card.component';
import { AiChatFeedbackComponent } from '../ai-chat-feedback/ai-chat-feedback.component';
import { AiChatThinkingBlockComponent } from '../ai-chat-thinking-block/ai-chat-thinking-block.component';

@Component({
  selector: 'qt-ai-chat-enhanced-message-bubble',
  standalone: true,
  imports: [
    MatIconModule,
    TranslationCoreModule,
    AiChatActionCardComponent,
    AiChatActionListComponent,
    AiChatChartCardComponent,
    AiChatFeedbackComponent,
    AiChatThinkingBlockComponent,
    StreamRevealDirective,
    AlloTableDirective,
  ],
  template: `
    <div
      class="qt-msg"
      [class.qt-msg--ai]="isBotMessage()"
      [class.qt-msg--user]="isUserMessage()">
      <!-- Tool-usage disclosure: a quiet one-liner above the answer that
           expands into a timeline of what ran (SD-2702). -->
      @if (hasToolsUsed() && !isTypingSignal()) {
        <div class="qt-tools-used">
          <button
            type="button"
            class="qt-tools-used__toggle"
            [attr.aria-expanded]="toolsExpanded()"
            (click)="toggleToolsExpanded()">
            <span class="qt-tools-used__summary">{{
              toolsUsedSummaryKey() | translate: [toolsUsed().length]
            }}</span>
            <mat-icon
              class="qt-tools-used__chevron"
              [class.qt-tools-used__chevron--open]="toolsExpanded()"
              aria-hidden="true">
              chevron_right
            </mat-icon>
          </button>
          @if (toolsExpanded()) {
            <ul class="qt-tools-used__list">
              @for (tool of toolsUsed(); track tool.toolName) {
                <li
                  class="qt-tools-used__item"
                  [class.qt-tools-used__item--failed]="tool.failed">
                  <span class="qt-tools-used__label">{{ tool.label }}</span>
                  <span class="qt-tools-used__duration">{{
                    formatDuration(tool.durationMs)
                  }}</span>
                </li>
              }
            </ul>
          }
        </div>
      }

      @if ((isBotMessage() && !isStoppedEmpty()) || hasMessageContent()) {
        <div
          class="qt-msg__bubble"
          [class.qt-msg__bubble--typing]="isTypingSignal()">
          <!-- Message content with custom components -->
          @if (hasMessageContent()) {
            <div
              class="qt-msg__content"
              (click)="onMessageContentClick($event)">
              @for (part of messageParts(); track trackByPart($index, part)) {
                <div class="qt-msg__part">
                  <!-- Action List Component -->
                  @if (
                    part.type === 'action_list' && isActionListData(part.data)
                  ) {
                    <qt-ai-chat-action-list [data]="part.data">
                    </qt-ai-chat-action-list>
                  }

                  <!-- Thinking Block Component -->
                  @if (part.type === 'thinking') {
                    <qt-ai-chat-thinking-block [content]="part.content">
                    </qt-ai-chat-thinking-block>
                  }

                  <!-- Action Card (e.g. send-email) -->
                  @if (part.type === 'action_card' && part.data) {
                    <qt-ai-chat-action-card
                      [payload]="part.data"
                      [userPromptText]="previousMessageText()">
                    </qt-ai-chat-action-card>
                  }

                  <!-- Inline chart (SD-2674) -->
                  @if (part.type === 'chart' && isChartSpec(part.data)) {
                    <qt-ai-chat-chart-card [rawSpec]="part.data">
                    </qt-ai-chat-chart-card>
                  }

                  <!-- Chart still streaming in, or truncated for good -->
                  @if (part.type === 'chart_loading') {
                    @if (isStreaming()) {
                      <div class="qt-chart-loading" role="status">
                        <span
                          class="qt-chart-loading__dot"
                          aria-hidden="true"></span>
                        <span>Building chart…</span>
                      </div>
                    } @else {
                      <p class="qt-chart-failed">Couldn't render that chart.</p>
                    }
                  }

                  <!-- Regular HTML content. The attribute gates the reveal
                       animations to streaming repaints so settled content
                       (final event, history load) renders still. -->
                  @if (part.type === 'html') {
                    <div
                      qtStreamReveal
                      qtAlloTable
                      [attr.data-qt-streaming]="isStreaming() ? '' : null"
                      [innerHTML]="part.htmlContent"></div>
                  }
                </div>
              }
            </div>
          }

          <!-- Thinking dots: alone before the first token, then a persistent
               tail under the committed answer until the turn completes. Tool
               progress reads as its own quiet line ABOVE the dots — a static
               dot beside the bouncing ones read as broken. -->
          @if (showThinkingTail()) {
            @if (toolStatusLabel()) {
              <div class="qt-tool-event-label" role="status">
                {{ toolStatusLabel() }}
              </div>
            }
            <div class="qt-typing-row">
              <span
                class="qt-typing-dots"
                role="status"
                [attr.aria-label]="'GNR_ALLO_THINKING' | translate">
                @for (
                  dot of typingDots();
                  track trackByDot($index, dot);
                  let i = $index
                ) {
                  <span
                    class="qt-typing-dot"
                    [style.animationDelay.ms]="i * 160">
                  </span>
                }
              </span>
            </div>
          }
        </div>
      }

      @if (showAttachmentIcon()) {
        <div class="qt-msg__attachments-row">
          @if (attachmentNames().length > 0) {
            @for (name of attachmentNames(); track $index) {
              <span class="qt-msg__attachment-pill">
                <mat-icon class="qt-msg__attachment-pill-icon"
                  >attach_file</mat-icon
                >
                <span class="qt-msg__attachment-pill-name" [title]="name">{{
                  name
                }}</span>
              </span>
            }
          } @else {
            <span class="qt-msg__attachment-pill">
              <mat-icon class="qt-msg__attachment-pill-icon"
                >attach_file</mat-icon
              >
              <span class="qt-msg__attachment-pill-name"
                >{{ attachmentCount() }} file{{
                  attachmentCount() > 1 ? 's' : ''
                }}</span
              >
            </span>
          }
        </div>
      }

      <!--
        SD-2487: a stopped turn is not a failure and must not be dressed as one. Quiet grey marker,
        no warning icon, and retry moves down to the message actions. Genuine failures keep the
        red treatment below, because there the alarm is the point.
      -->
      @if (isStopped()) {
        <div class="qt-msg__stopped">
          <span class="qt-msg__stopped-marker" aria-hidden="true"></span>
          <span class="qt-msg__stopped-text">{{
            interruptedTextKey() | translate
          }}</span>
          @if (isStoppedEmpty()) {
            <button type="button" class="qt-msg__retry" (click)="retry.emit()">
              <mat-icon class="qt-msg__retry-icon">refresh</mat-icon>
              <span>{{ 'GNR_ALLO_RETRY' | translate }}</span>
            </button>
          }
        </div>
      }

      @if (isFailed()) {
        <div class="qt-msg__interrupted">
          <mat-icon class="qt-msg__interrupted-icon">error_outline</mat-icon>
          <span class="qt-msg__interrupted-text">{{
            interruptedTextKey() | translate
          }}</span>
          <button type="button" class="qt-msg__retry" (click)="retry.emit()">
            <mat-icon class="qt-msg__retry-icon">refresh</mat-icon>
            <span>{{ 'GNR_ALLO_RETRY' | translate }}</span>
          </button>
        </div>
      }

      @if (showFeedback()) {
        <qt-ai-chat-feedback
          [chatId]="feedbackChatId()"
          [ts]="feedbackTs()"
          [messageText]="feedbackMessageText()"
          [showRetry]="isStopped()"
          (retry)="retry.emit()">
        </qt-ai-chat-feedback>
      }
    </div>
  `,
  styleUrls: ['./ai-chat-enhanced-message-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatEnhancedMessageBubbleComponent {
  // Signal-based inputs
  readonly message = input<ChatMsgVM>();
  readonly previousMessageText = input<string>('');
  readonly isTyping = input(false);
  readonly typingDots = input<readonly number[]>([0, 1, 2]);
  readonly toolStatusLabel = input<string | null>(null);

  /** SD-2450: emitted when the user clicks Retry on an interrupted bubble. */
  readonly retry = output<void>();

  // Signals for reactive state management
  private readonly messageSignal = signal<ChatMsgVM | undefined>(undefined);
  readonly isTypingSignal = signal(false);

  // Computed signals
  readonly messageParts = computed(() => {
    const message = this.messageSignal();
    if (!message) return [];

    const structuredCards = this.structuredActionCardParts(message);
    // SD-2865: a turn can now deliver a card with no reply text at all — the
    // model writing nothing no longer means there is nothing to render.
    if (!message.text) return structuredCards;

    return this.processMessageParts(
      this.committedText(message),
      structuredCards
    );
  });

  /**
   * SD-2865: card parts built from the message's structured `actionCards`, which
   * the BE captured when each tool returned (SD-2798). Empty for events written
   * before that, which carry their cards inside the text instead.
   */
  private structuredActionCardParts(message: ChatMsgVM): MessagePart[] {
    return (message.actionCards ?? [])
      .map(json => this.markdownRenderer.parseStructuredActionCard(json))
      .filter((card): card is Record<string, unknown> => card !== null)
      .map(card => ({ type: 'action_card' as const, data: card }));
  }

  /**
   * SD-2674: while a turn is streaming, render only up to the last sentence
   * boundary and hold the trailing fragment back until its closer arrives.
   * Blocks then fade up a sentence at a time instead of characters twitching
   * into view. The typing indicator stays visible for the whole turn, so
   * holding back the first sentence never leaves the bubble looking stalled.
   *
   * A closing `</chart>` also counts as a commit boundary, so a chart-only
   * reply (which carries no sentence punctuation) renders as soon as its block
   * closes rather than staying blank until `complete`.
   *
   * An unterminated `<chart>` block is exempt — the `chart_loading` part
   * already renders a placeholder for it, and truncating would hide it.
   */
  private committedText(message: ChatMsgVM): string {
    const text = message.text;
    if (!message.streaming) return text;

    const lowered = text.toLowerCase();
    const chartOpenIndex = lowered.lastIndexOf('<chart>');
    if (chartOpenIndex >= 0 && !/<\/chart>/i.test(text.slice(chartOpenIndex))) {
      return text;
    }

    // Index of the last character to commit (inclusive).
    const closeTag = '</chart>';
    const chartCloseIndex = lowered.lastIndexOf(closeTag);
    const boundary = Math.max(
      text.lastIndexOf('\n'),
      text.lastIndexOf('. '),
      text.lastIndexOf('! '),
      text.lastIndexOf('? '),
      chartCloseIndex >= 0 ? chartCloseIndex + closeTag.length - 1 : -1
    );

    return boundary >= 0 ? text.slice(0, boundary + 1) : '';
  }

  // Helper method to check if data is ActionListData
  isActionListData(data: unknown): data is ActionListData {
    return (
      typeof data === 'object' &&
      data !== null &&
      'type' in data &&
      (data as ActionListData).type === 'action_list' &&
      'itemList' in data &&
      Array.isArray((data as ActionListData).itemList)
    );
  }

  readonly isBotMessage = computed(
    () => this.isTypingSignal() || this.messageSignal()?.author === 'bot'
  );

  readonly isUserMessage = computed(
    () => !this.isTypingSignal() && this.messageSignal()?.author === 'user'
  );

  readonly hasMessageContent = computed(
    () => !this.isTypingSignal() && !!this.messageSignal()?.text
  );

  /**
   * SD-2674: a `chart_loading` part means the `<chart>` block has no closing
   * tag. Mid-stream that is simply "not arrived yet"; on a settled message it
   * means the block was truncated for good (BE token cap / interrupt), so the
   * bubble shows the same failed-state copy as the chart card instead of a
   * placeholder that would spin forever on every reload.
   */
  readonly isStreaming = computed(() => !!this.messageSignal()?.streaming);

  /**
   * SD-2674: the thinking dots stay put for the whole turn — alone in an empty
   * bubble before the first token, then as a tail beneath the committed answer
   * while it grows — and disappear only when the turn completes or fails.
   */
  readonly showThinkingTail = computed(
    () =>
      this.isTypingSignal() ||
      (this.isStreaming() && !this.messageSignal()?.errored)
  );

  readonly showAttachmentIcon = computed(
    () =>
      !this.isTypingSignal() &&
      this.messageSignal()?.author === 'user' &&
      (this.messageSignal()?.attachmentCount ?? 0) > 0
  );

  readonly attachmentNames = computed(
    () => this.messageSignal()?.attachmentNames ?? []
  );

  readonly attachmentCount = computed(
    () => this.messageSignal()?.attachmentCount ?? 0
  );

  /** SD-2450: the turn was interrupted/failed mid-stream. */
  readonly isErrored = computed(
    () => !this.isTypingSignal() && !!this.messageSignal()?.errored
  );

  /**
   * SD-2487: the user stopped the turn (or it hit the server's wall-clock cap).
   *
   * Shares the `errored` flag with real failures — that is how the backend marks a terminal turn —
   * but is not one, and the two are presented differently from here on.
   */
  readonly isStopped = computed(
    () => this.isErrored() && this.messageSignal()?.errorCode === 'cancelled'
  );

  /**
   * SD-2819: stopped before any text arrived, typically during a tool call. There is nothing to
   * copy or rate, so the message actions (where a stopped turn's retry lives) stay hidden; retry
   * goes on the stopped line instead, and the empty bubble is not drawn.
   */
  readonly isStoppedEmpty = computed(
    () => this.isStopped() && !this.messageSignal()?.text
  );

  /** Something actually went wrong: keeps the alarm styling and its own inline retry. */
  readonly isFailed = computed(() => this.isErrored() && !this.isStopped());

  /**
   * Copy key for the interrupted notice — varies by failure class.
   *
   * SD-2765: `cancelled` is not a failure. The user stopped the turn (or it hit the server's
   * wall-clock cap), so the bubble treatment is identical but the copy must not suggest something
   * went wrong.
   */
  readonly interruptedTextKey = computed(() => {
    switch (this.messageSignal()?.errorCode) {
      case 'cancelled':
        return 'GNR_ALLO_STREAM_STOPPED';
      case 'failed':
        return 'GNR_ALLO_STREAM_FAILED';
      default:
        return 'GNR_ALLO_STREAM_INTERRUPTED';
    }
  });

  /**
   * SD-2487: stopped turns now show the message actions too, since that is where retry lives and
   * a partial answer is still worth copying or rating. Real failures stay excluded — there is
   * nothing to rate, and retry has its own control on the error row.
   */
  readonly showFeedback = computed(
    () =>
      !this.isTypingSignal() &&
      !this.messageSignal()?.streaming &&
      !this.isFailed() &&
      this.messageSignal()?.author === 'bot' &&
      !!this.messageSignal()?.text &&
      !!this.messageSignal()?.chatId &&
      this.messageSignal()?.ts != null
  );

  readonly feedbackChatId = computed(() => this.messageSignal()?.chatId ?? '');
  readonly feedbackTs = computed(() => this.messageSignal()?.ts ?? 0);
  readonly feedbackMessageText = computed(
    () => this.messageSignal()?.text ?? ''
  );

  readonly toolsUsed = computed(() => this.messageSignal()?.toolsUsed ?? []);
  readonly hasToolsUsed = computed(() => this.toolsUsed().length > 0);
  readonly toolsUsedSummaryKey = computed(() =>
    this.toolsUsed().length === 1
      ? 'GNR_ALLO_TOOL_USED_ONE'
      : 'GNR_ALLO_TOOLS_USED'
  );
  readonly toolsExpanded = signal(false);

  // Signal-based injection
  private readonly markdownRenderer = inject(MarkdownRendererService);
  private readonly actionHandler = inject(AiChatActionHandlerService);

  constructor() {
    // Effect to sync input properties with signals
    effect(() => {
      this.messageSignal.set(this.message());
      this.isTypingSignal.set(this.isTyping());
    });
  }

  toggleToolsExpanded(): void {
    this.toolsExpanded.update(v => !v);
  }

  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  trackByDot: TrackByFunction<number> = (index: number) => index;
  trackByPart: TrackByFunction<{
    type:
      | 'html'
      | 'action_list'
      | 'thinking'
      | 'action_card'
      | 'chart'
      | 'chart_loading';
    htmlContent?: SafeHtml;
    content?: string;
    data?: ActionListData | { content: string } | unknown;
  }> = (index: number, part) => `${part.type}-${index}`;

  /** Chart parts carry the raw `<chart>` JSON string. */
  isChartSpec(data: unknown): data is string {
    return typeof data === 'string' && data.length > 0;
  }

  onMessageContentClick(event: MouseEvent): void {
    this.actionHandler.onMessageContentClick(event);
  }

  private processMessageParts(
    text: string,
    structuredCards: MessagePart[] = []
  ): MessagePart[] {
    if (!text) return structuredCards;

    const customComponents =
      this.markdownRenderer.extractCustomComponents(text);

    if (customComponents.length === 0) {
      // SD-2865: a card delivered structurally, with the model's reply summarising
      // it instead of echoing it — the case this whole change exists for. Show the
      // card and drop the summary, as an echoed card has always done.
      if (structuredCards.length > 0) return structuredCards;

      // No custom components, render as regular HTML
      return [
        {
          type: 'html',
          htmlContent: this.markdownRenderer.renderToHtml(text),
        },
      ];
    }

    // Process text with custom components
    let processedText = text;
    const parts: MessagePart[] = [];

    // Replace custom components with placeholders and track their positions
    const componentMap = new Map<string, unknown>();
    customComponents.forEach((component, index) => {
      const placeholder = `__COMPONENT_${index}__`;
      componentMap.set(placeholder, component);
      processedText = processedText.replace(
        component.originalMatch,
        placeholder
      );
    });

    // Split by placeholders and process each part
    const textParts = processedText.split(/(__COMPONENT_\d+__)/);

    textParts.forEach(part => {
      if (part.startsWith('__COMPONENT_') && part.endsWith('__')) {
        const component = componentMap.get(part) as {
          type: string;
          data: ActionListData | { content: string } | unknown;
        };
        if (component) {
          if (component.type === 'action_list') {
            parts.push({
              type: 'action_list',
              data: component.data as ActionListData,
            });
          } else if (component.type === 'thinking') {
            parts.push({
              type: 'thinking',
              content: (component.data as { content: string }).content,
            });
          } else if (component.type === 'action_card') {
            parts.push({
              type: 'action_card',
              data: component.data,
            });
          } else if (component.type === 'chart') {
            parts.push({
              type: 'chart',
              data: component.data,
            });
          } else if (component.type === 'chart_loading') {
            parts.push({ type: 'chart_loading' });
          }
        }
      } else if (part.trim()) {
        parts.push({
          type: 'html',
          htmlContent: this.markdownRenderer.renderToHtml(part),
        });
      }
    });

    // SD-2865: when the BE delivered the cards as structured data, they are the
    // ones to render. Any <action-card> block in the text is the model's echo of
    // the same card, so dropping it is what keeps a card from rendering twice.
    if (structuredCards.length > 0) {
      const nonCardParts = parts.filter(p => p.type !== 'action_card');
      return [
        ...structuredCards,
        ...nonCardParts.filter(p => p.type !== 'html'),
      ];
    }

    // When the backend sends an action_card, show only the card(s) and hide the intro/summary text
    const hasActionCard = parts.some(p => p.type === 'action_card');
    if (hasActionCard) {
      return parts.filter(p => p.type !== 'html');
    }

    return parts;
  }
}
